"use client";

import { FormEvent, useState } from "react";
import { apiFetch } from "../lib/api";

export default function HelpButton() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("help");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSending(true);
    setStatus("");
    try {
      const response = await apiFetch("/support/requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category, subject, message }),
      });
      const data = await response.json();
      if (!response.ok) {
        setStatus(typeof data.detail === "string" ? data.detail : "Unable to send your request.");
        return;
      }
      setStatus("Thanks — your request was sent.");
      setSubject("");
      setMessage("");
    } catch {
      setStatus("Unable to send your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button onClick={() => { setOpen(true); setStatus(""); }} className="px-1.5 sm:px-3 py-1.5 rounded-md text-zinc-300 hover:text-orange-400 hover:bg-zinc-800 font-semibold transition-colors text-[11px] sm:text-base whitespace-nowrap">
        ❓ <span className="hidden sm:inline">Help</span>
      </button>
      {open && (
        <div className="fixed inset-0 z-[100] bg-zinc-950/75 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="help-title">
          <form onSubmit={submit} className="w-full max-w-lg bg-zinc-800 border border-zinc-600 rounded-xl p-6 shadow-2xl">
            <div className="flex justify-between gap-4 mb-2">
              <h2 id="help-title" className="text-xl font-bold text-orange-400">Send feedback or get help</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-100 text-xl" aria-label="Close">×</button>
            </div>
            <p className="text-sm text-zinc-400 mb-5">Report a bug, ask for help, or share an idea. We’ll reply to the email on your account.</p>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select value={category} onChange={(event) => setCategory(event.target.value)} className="w-full bg-zinc-950 border border-zinc-600 rounded px-3 py-2.5 mb-4 focus:outline-none focus:border-orange-400">
              <option value="help">Help request</option>
              <option value="bug">Bug report</option>
              <option value="idea">Idea / feedback</option>
            </select>
            <label className="block text-sm font-medium mb-1">Subject</label>
            <input required maxLength={160} value={subject} onChange={(event) => setSubject(event.target.value)} className="w-full bg-zinc-950 border border-zinc-600 rounded px-3 py-2.5 mb-4 focus:outline-none focus:border-orange-400" placeholder="Briefly describe the issue" />
            <label className="block text-sm font-medium mb-1">Message</label>
            <textarea required maxLength={5000} value={message} onChange={(event) => setMessage(event.target.value)} className="w-full min-h-32 bg-zinc-950 border border-zinc-600 rounded px-3 py-2.5 focus:outline-none focus:border-orange-400" placeholder="What happened, what you expected, and any useful details." />
            {status && <p className={`text-sm mt-4 ${status.startsWith("Thanks") ? "text-green-400" : "text-red-400"}`}>{status}</p>}
            <div className="flex justify-end gap-3 mt-5">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-zinc-300 hover:text-white">Cancel</button>
              <button disabled={sending} className="px-5 py-2 rounded bg-orange-600 hover:bg-orange-500 disabled:opacity-50 font-bold">{sending ? "Sending…" : "Send request"}</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
